import { Button, Form, Input, Modal, InputNumber, DatePicker, Select, Switch, message } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGetOrElse, nullSafeGet, isAdminUser } from '../../utils/DataAccessUtils';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import {
	ACC_AUDIT_ACTIONABLE_STATUSES,
	ACC_AUDIT_CHANGE_APPLY_STATUSES,
	ACC_AUDIT_KEY_DISPLAY_NAMES,
} from '../../utils/AssetCycleUtils';
import { ASSET_FIELDS_LABELS_MAP, ASSET_FIELDS_DETAILS } from '../../utils/assetUtils';
import OWUpload from '../OWUpload';
import { imageUploadValidation, normFile } from '../../utils/ImageUtils';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';
import { PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getCurrency } from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import {
	assetModelsRestCrudThunksForBuyer,
	assetModelsRestCrudThunksForSupplier,
} from '../../thunks/asset_models_thunks';
import {
	assetTypesRestCrudThunksForBuyer,
	assetTypesRestCrudThunksForSupplier,
} from '../../thunks/asset_types_thunks';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';
import ScannedAssetDataSection from './ScannedAssetDataSection';
import SeparatorWithText from '../common/SeperatorWithText';
import ACCAuditStatusSelect from './ACCAuditStatusSelect';

const ACTION_LABELS = {
	assignQRCodeAndRemoveFromMatch: 'Mark to assign QR & remove from match',
	swapQRCodes: 'Mark to swap QR codes',
	applyNewQRCode: 'Mark to apply new QR code',
	needsFurtherReview: 'Needs further review',
	qrCodeRemoved: 'QR code removed',
};

const FORM_NAME = 'asset-audit-form';
const dateFormat = 'MMMM DD, YYYY';

function AssetAuditForm({
	assetAudit,
	cycleRun,
	onCancel,
	onSubmit,
	assetModels,
	fetchAssetModels,
	fetchMultipleAssetModels,
	assetTypes,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	currentUser,
	userType,
}) {
	const [form] = Form.useForm();
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [updating, setUpdating] = useState(false);
	const [formChanged, setFormChanged] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState(() => {
		const status = nullSafeGetOrElse(
			'changeApplyStatus',
			assetAudit,
			ACC_AUDIT_CHANGE_APPLY_STATUSES.WaitingForApproval
		);
		return ACC_AUDIT_ACTIONABLE_STATUSES.includes(status)
			? status
			: ACC_AUDIT_CHANGE_APPLY_STATUSES.WaitingForApproval;
	});

	const capturedFields = useMemo(
		() => nullSafeGetOrElse('capturedFields', assetAudit, []),
		[assetAudit]
	);

	const captureFields = useMemo(
		() => nullSafeGetOrElse('assetCycleCountEvent.captureFields', cycleRun, []),
		[cycleRun]
	);

	const qrCodeImagesField = useMemo(
		() =>
			captureFields.find((field) => {
				const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
				return fieldDetails && fieldDetails.key === 'qrCodeImages';
			}),
		[captureFields]
	);

	const serialNumberImagesField = useMemo(
		() =>
			captureFields.find((field) => {
				const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
				return fieldDetails && fieldDetails.key === 'serialNumberImages';
			}),
		[captureFields]
	);

	const assetImagesField = useMemo(
		() =>
			captureFields.find((field) => {
				const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
				return fieldDetails && fieldDetails.key === 'assetImages';
			}),
		[captureFields]
	);

	const hasQrCodeImages = !!qrCodeImagesField;
	const hasSerialNumberImages = !!serialNumberImagesField;
	const hasAssetImages = !!assetImagesField;

	const isAdmin = useMemo(() => isAdminUser(currentUser), [currentUser]);

	const allowToEditAssetName = useMemo(() => {
		if (isAdmin) return true;
		const event = nullSafeGet('assetCycleCountEvent', cycleRun);
		if (event && 'allowToEditAssetName' in event) {
			return event.allowToEditAssetName;
		}
		return true;
	}, [cycleRun, isAdmin]);

	const allowToEditAssetType = useMemo(() => {
		if (isAdmin) return true;
		const event = nullSafeGet('assetCycleCountEvent', cycleRun);
		if (event && 'allowToEditAssetType' in event) {
			return event.allowToEditAssetType;
		}
		return true;
	}, [cycleRun, isAdmin]);

	const handlePreview = useCallback((file) => {
		setPreviewImage(file.url || file.thumbUrl);
		setPreviewVisible(true);
	}, []);

	const handleCancel = useCallback(() => {
		setPreviewVisible(false);
	}, []);

	// Get assetTypeId from the form field so it updates when user changes asset type
	const originalAssetTypeId = useMemo(() => {
		return (
			nullSafeGet('asset.assetTypeId', assetAudit) || nullSafeGet('asset.assetType.id', assetAudit)
		);
	}, [assetAudit]);
	const formAssetTypeId = Form.useWatch('assetTypeId', form);
	const assetTypeId = formAssetTypeId || originalAssetTypeId;

	// Get currency from currentUser
	const currency = useMemo(() => {
		return getCurrency({ currentUser });
	}, [currentUser]);

	// Render appropriate input component based on field type from ASSET_FIELDS_DETAILS
	const renderFieldInput = useCallback(
		(field) => {
			const fieldKey = field.key;
			// Get field type from ASSET_FIELDS_DETAILS or from field definition
			const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === fieldKey);
			const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');

			// Handle assetModelId with full async select functionality (same as AssetForm)
			// Check if field exists in ASSET_FIELDS_DETAILS and is assetModelId
			if (fieldDetails && fieldDetails.key === 'assetModelId') {
				return (
					<OWAsyncSelect
						targetCollectionName="ASSET_MODELS_AUTOCOMPLETE"
						stateSlice={assetModels}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleAssetModels(ids, targetCollectionName);
						}}
						fetchData={(searchText, targetCollectionName) => {
							fetchAssetModels(
								{
									modelName: searchText,
									assetTypeId: assetTypeId,
								},
								targetCollectionName
							);
						}}
						placeholder={'Select Model'}
						renderRecord={(model) => (
							<Select.Option key={model.id} value={model.id}>
								{model.modelName}
							</Select.Option>
						)}
						additionalFilters={{
							assetTypeId: assetTypeId,
						}}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				);
			}

			switch (fieldType) {
				case 'date':
					return <DatePicker format={dateFormat} style={{ width: '100%' }} />;
				case 'currency':
					return (
						<InputNumber
							addonAfter={currency && currency.id ? currency.id : 'USD'}
							style={{ width: '100%' }}
						/>
					);
				case 'switch':
					return <Switch />;
				case 'text':
				case 'textScanner':
				default:
					return <Input />;
			}
		},
		[assetModels, fetchAssetModels, fetchMultipleAssetModels, assetTypeId, currency]
	);

	// Convert [{fileName, fileId}] to fileList format for Ant Design Upload
	const toFileList = (images) => {
		if (!images || !Array.isArray(images)) return [];
		return images
			.filter((img) => img.fileId && img.fileName)
			.map((img, idx) => ({
				uid: `-${idx}-${img.fileId}`,
				name: img.fileName,
				status: 'done',
				url: getProtectedImageUriGeneric(img.fileId, img.fileName),
				thumbUrl: getProtectedImageUriGeneric(img.fileId, img.fileName),
				response: { data: { fileName: img.fileName, fileId: img.fileId } },
			}));
	};

	// Convert fileList format back to [{fileName, fileId}]
	const fromFileList = (fileList) => {
		if (!fileList || !Array.isArray(fileList)) return [];
		return fileList
			.map((file) => {
				const data = file.response && file.response.data ? file.response.data : null;
				return data && data.fileName && data.fileId
					? { fileName: data.fileName, fileId: data.fileId }
					: null;
			})
			.filter(Boolean);
	};

	const handleValuesChange = useCallback(
		(changedValues) => {
			setFormChanged(true);
			// Clear asset model when asset type changes to prevent inconsistent selection
			if (changedValues.assetTypeId !== undefined) {
				form.setFieldsValue({ assetModelId: undefined });
			}
		},
		[form]
	);

	const handleSubmit = useCallback(
		(values) => {
			if (onSubmit) {
				const updatedCapturedFields = captureFields
					.filter((field) => {
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
						const fieldType = nullSafeGetOrElse('type', fieldDetails, null);
						return fieldType !== 'images';
					})
					.map((field) => {
						const fieldValue = values[field.key];
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
						const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');

						// Process value matching AssetForm - only convert specific fields
						let processedValue = fieldValue;

						// Convert assetModelId to integer (matching AssetForm line 307)
						if (fieldDetails && fieldDetails.key === 'assetModelId' && processedValue) {
							processedValue = parseInt(processedValue, 10);
						}

						// Convert currency fields to float (matching AssetForm lines 314-315)
						if (fieldType === 'currency' && processedValue) {
							processedValue = parseFloat(processedValue);
						}

						return {
							key: field.key,
							value: processedValue === '' || processedValue === null ? undefined : processedValue,
						};
					});

				if (allowToEditAssetName && values.assetName !== undefined) {
					updatedCapturedFields.push({ key: 'name', value: values.assetName });
				}
				if (
					allowToEditAssetType &&
					values.assetTypeId !== undefined &&
					values.assetTypeId !== null
				) {
					updatedCapturedFields.push({ key: 'assetTypeId', value: String(values.assetTypeId) });
				}

				const patchData: any = {
					capturedFields: updatedCapturedFields,
				};

				if (values.notes !== undefined) {
					patchData.notes = values.notes;
				}
				if (values.qrCodeImages) {
					patchData.qrCodeImages = fromFileList(values.qrCodeImages);
				}
				if (values.serialNumberImages) {
					patchData.serialNumberImages = fromFileList(values.serialNumberImages);
				}
				if (values.assetImages) {
					patchData.assetImages = fromFileList(values.assetImages);
				}
				setUpdating(true);
				onSubmit(patchData, selectedStatus)
					.catch(message.error)
					.finally(() => setUpdating(false));
			}
		},
		[onSubmit, captureFields, selectedStatus, allowToEditAssetName, allowToEditAssetType]
	);

	const initialValues = useMemo(() => {
		const capturedFieldsMap = capturedFields.reduce((acc, field) => {
			acc[field.key] = field.value;
			return acc;
		}, {});
		const capturedFieldKeys = new Set(capturedFields.map((field) => field.key));
		const asset = nullSafeGetOrElse('asset', assetAudit, {});
		const shouldPrefillFromAsset =
			nullSafeGetOrElse('assetCycleCountEvent.prefillFields', cycleRun, false) === true;

		const resolveFieldValue = (fieldKey) => {
			if (capturedFieldKeys.has(fieldKey)) {
				return capturedFieldsMap[fieldKey];
			}
			if (shouldPrefillFromAsset) {
				const assetValue = asset[fieldKey];
				return assetValue === null ? undefined : assetValue;
			}
			return undefined;
		};

		const values = captureFields.reduce((acc, field) => {
			const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
			const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');

			if (fieldType === 'images') {
				const images = nullSafeGetOrElse(field.key, assetAudit, []);
				acc[field.key] = toFileList(images);
			} else {
				const fieldValue = resolveFieldValue(field.key);
				const resolvedFieldType = field.type || field.valueType || fieldType;

				// Convert date strings to moment objects for DatePicker
				if (resolvedFieldType === 'date' && fieldValue) {
					acc[field.key] = moment(fieldValue);
				} else {
					acc[field.key] = fieldValue !== undefined ? fieldValue : undefined;
				}
			}
			return acc;
		}, {});

		const nameCaptured = resolveFieldValue('name');
		const assetTypeIdCaptured = resolveFieldValue('assetTypeId');

		return {
			...values,
			notes: nullSafeGetOrElse('notes', assetAudit, ''),
			qrCodeImages: toFileList(nullSafeGetOrElse('qrCodeImages', assetAudit, [])),
			serialNumberImages: toFileList(nullSafeGetOrElse('serialNumberImages', assetAudit, [])),
			assetImages: toFileList(nullSafeGetOrElse('assetImages', assetAudit, [])),
			assetName:
				nameCaptured !== undefined ? nameCaptured : nullSafeGetOrElse('asset.name', assetAudit, ''),
			assetTypeId:
				assetTypeIdCaptured !== undefined
					? assetTypeIdCaptured
					: nullSafeGet('asset.assetTypeId', assetAudit) ||
					  nullSafeGet('asset.assetType.id', assetAudit),
		};
	}, [captureFields, capturedFields, assetAudit, cycleRun]);

	const needsVerificationLabel = nullSafeGet('needsVerificationAction', assetAudit)
		? ACTION_LABELS[assetAudit.needsVerificationAction]
		: null;

	return (
		<Modal
			title={
				<div className="flex flex-col gap-2">
					<div className="h4medium">
						{nullSafeGetOrElse('asset.name', assetAudit, '')},{' '}
						{nullSafeGetOrElse('asset.assetType.name', assetAudit, '')}
					</div>
					<div className="flex flex-row items-center gap-2">
						<GeneralStatusDisplay
							statusKey={assetAudit.matchStatus}
							statusLabel={nullSafeGetOrElse(
								assetAudit.matchStatus,
								ACC_AUDIT_KEY_DISPLAY_NAMES,
								assetAudit.matchStatus
							)}
						/>
						{needsVerificationLabel ? (
							<span className="footnoteMedium text-[#DB8556]">{needsVerificationLabel}</span>
						) : null}
					</div>
				</div>
			}
			visible
			onCancel={onCancel}
			bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
			footer={
				<div className="flex flex-row items-center">
					<ACCAuditStatusSelect value={selectedStatus} onChange={setSelectedStatus} />
					<div className="ml-auto flex flex-row items-center gap-3">
						<Button key="cancel" onClick={onCancel} size="large">
							Cancel
						</Button>
						<Button
							key="submit"
							type="primary"
							size="large"
							htmlType="submit"
							form={FORM_NAME}
							loading={updating}
							disabled={!formChanged}
						>
							Save Changes
						</Button>
					</div>
				</div>
			}
		>
			<ScannedAssetDataSection assetAudit={assetAudit} userType={userType} />
			<div className="mt-4 flex flex-col">
				<span className="h5medium">Fields Captured:</span>
				<SeparatorWithText className="mb-4 mt-2" />
			</div>
			<Form
				key={assetAudit && assetAudit.id ? assetAudit.id : 'form'}
				id={FORM_NAME}
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={initialValues}
				onValuesChange={handleValuesChange}
			>
				{allowToEditAssetName ? (
					<Form.Item
						name="assetName"
						label="Asset Name"
						rules={[{ required: true, message: 'Asset name is required' }]}
					>
						<Input placeholder="Enter asset name" />
					</Form.Item>
				) : null}
				{allowToEditAssetType ? (
					<Form.Item
						name="assetTypeId"
						label="Asset Type"
						rules={[{ required: true, message: 'Asset type is required' }]}
					>
						<OWAsyncSelect
							stateSlice={assetTypes}
							targetCollectionName="ASSET_AUDIT_FORM_ASSET_TYPES_AUTOCOMPLETE"
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleAssetTypes(ids, targetCollectionName);
							}}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) => {
								fetchAssetTypes(
									{ name: searchText || undefined },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
							}}
							placeholder="Select asset type"
							renderRecord={(assetType) => (
								<Select.Option key={assetType.id} value={assetType.id}>
									{assetType.name}
								</Select.Option>
							)}
							sortBy={{ sort_by: 'name', order: 'ascend' }}
						/>
					</Form.Item>
				) : null}
				{captureFields
					.filter((field) => {
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
						const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');
						return fieldType !== 'images';
					})
					.map((field) => {
						const fieldKey = field.key;
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === fieldKey);
						const fieldType =
							field.type || field.valueType || nullSafeGetOrElse('type', fieldDetails, 'text');
						const isBoolean = fieldType === 'switch';

						return (
							<Form.Item
								key={field.key}
								label={ASSET_FIELDS_LABELS_MAP[field.key] || field.key}
								name={field.key}
								valuePropName={isBoolean ? 'checked' : 'value'}
								rules={
									field.isRequired
										? [
												{
													required: true,
													message: `${ASSET_FIELDS_LABELS_MAP[field.key] || field.key} is required`,
												},
										  ]
										: undefined
								}
							>
								{renderFieldInput(field)}
							</Form.Item>
						);
					})}
				<Form.Item name="notes" label="Additional Notes">
					<Input.TextArea />
				</Form.Item>
				{hasQrCodeImages ? (
					<Form.Item
						name="qrCodeImages"
						label={ASSET_FIELDS_LABELS_MAP['qrCodeImages']}
						valuePropName="fileList"
						getValueFromEvent={normFile}
						rules={
							qrCodeImagesField && qrCodeImagesField.isRequired
								? [
										{
											required: true,
											message: `${ASSET_FIELDS_LABELS_MAP['qrCodeImages']} is required`,
										},
								  ]
								: undefined
						}
					>
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="avatar-uploader"
							beforeUpload={imageUploadValidation}
							onPreview={handlePreview}
							maxCount={1}
						>
							<div style={{ display: 'inline' }}>
								{<PlusOutlined />}
								<div className="ant-upload-text">Upload</div>
							</div>
						</OWUpload>
					</Form.Item>
				) : null}
				{hasSerialNumberImages ? (
					<Form.Item
						name="serialNumberImages"
						label={ASSET_FIELDS_LABELS_MAP['serialNumberImages']}
						valuePropName="fileList"
						getValueFromEvent={normFile}
						rules={
							serialNumberImagesField && serialNumberImagesField.isRequired
								? [
										{
											required: true,
											message: `${ASSET_FIELDS_LABELS_MAP['serialNumberImages']} is required`,
										},
								  ]
								: undefined
						}
					>
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="avatar-uploader"
							beforeUpload={imageUploadValidation}
							onPreview={handlePreview}
							maxCount={1}
						>
							<div style={{ display: 'inline' }}>
								{<PlusOutlined />}
								<div className="ant-upload-text">Upload</div>
							</div>
						</OWUpload>
					</Form.Item>
				) : null}
				{hasAssetImages ? (
					<Form.Item
						name="assetImages"
						label={ASSET_FIELDS_LABELS_MAP['assetImages']}
						valuePropName="fileList"
						getValueFromEvent={normFile}
						rules={
							assetImagesField && assetImagesField.isRequired
								? [
										{
											required: true,
											message: `${ASSET_FIELDS_LABELS_MAP['assetImages']} is required`,
										},
								  ]
								: undefined
						}
					>
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="avatar-uploader"
							beforeUpload={imageUploadValidation}
							onPreview={handlePreview}
							maxCount={1}
						>
							<div style={{ display: 'inline' }}>
								{<PlusOutlined />}
								<div className="ant-upload-text">Upload</div>
							</div>
						</OWUpload>
					</Form.Item>
				) : null}
				<Modal visible={previewVisible} footer={null} onCancel={handleCancel}>
					<img alt="preview" style={{ width: '100%' }} src={previewImage} />
				</Modal>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	assetModels: state.asset_models,
	assetTypes: state.asset_types,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAssetModels: (params, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetModelsRestCrudThunksForSupplier.readLite(
						{ ...params, no_pagination: true },
						targetCollectionName
				  )
				: assetModelsRestCrudThunksForBuyer.readLite(
						{ ...params, no_pagination: true },
						targetCollectionName
				  )
		),
	fetchMultipleAssetModels: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetModelsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: assetModelsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: assetTypesRestCrudThunksForBuyer.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleAssetTypes: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: assetTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(AssetAuditForm));
